import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';

import { listContacts } from '../contacts/api/contacts.client.js';
import { getDashboardSignals, getDashboardSignalsQueryKey, getDashboardStatsQueryKey, getDashboardStats, getDashboardActivityQueryKey, getDashboardActivity, getDashboardOnboardingQueryKey, getDashboardOnboarding } from './api/dashboard.client.js';
import { listDeals } from '../deals/api/deals.client.js';
import { listHotLeads } from '../hot-leads/api/hot-leads.client.js';
import { listTemplates } from '../templates/api/templates.client.js';
import { useCurrentScope } from '../../shared/scope/useCurrentScope.js';

import { buildDashboardViewModel, type DashboardViewModel } from './dashboard.vm.js';

export interface UseDashboardViewModelResult {
  viewModel: DashboardViewModel;
  dealsLoading: boolean;
  hotLeadsLoading: boolean;
}

export function useDashboardViewModel(): UseDashboardViewModelResult {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId;

  const dealsQuery = useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'deals'],
    queryFn: () => listDeals(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'contacts'],
    queryFn: () => listContacts(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  const hotLeadsQuery = useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'hot-leads'],
    queryFn: () => listHotLeads(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'templates'],
    queryFn: () => listTemplates(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  const signalsQuery = useQuery({
    queryKey: workspaceId ? getDashboardSignalsQueryKey(workspaceId) : ['dashboard', 'no-workspace', 'signals'],
    queryFn: () => getDashboardSignals(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const statsQuery = useQuery({
    queryKey: workspaceId ? getDashboardStatsQueryKey(workspaceId) : ['dashboard', 'no-workspace', 'stats'],
    queryFn: () => getDashboardStats(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const activityQuery = useQuery({
    queryKey: workspaceId ? getDashboardActivityQueryKey(workspaceId) : ['dashboard', 'no-workspace', 'activity'],
    queryFn: () => getDashboardActivity(10),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const onboardingQuery = useQuery({
    queryKey: workspaceId ? getDashboardOnboardingQueryKey(workspaceId) : ['dashboard', 'no-workspace', 'onboarding'],
    queryFn: () => getDashboardOnboarding(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  return {
    viewModel: buildDashboardViewModel({
      deals: dealsQuery.data ?? [],
      hotLeads: hotLeadsQuery.data ?? [],
      signals: signalsQuery.data ?? { scenarioRuns: [], pendingApprovals: [] },
      stats: statsQuery.data ?? { dealsTotal: 0, wonThisMonth: 0, hotLeadsCount: 0, avgDealValue: 0 },
      activities: activityQuery.data ?? [],
      onboarding: onboardingQuery.data ?? {
        id: '', userId: '', workspaceId: workspaceId ?? '', completedSteps: [], dismissed: false, updatedAt: ''
      },
      t,
    }),
    dealsLoading: dealsQuery.isLoading,
    hotLeadsLoading: hotLeadsQuery.isLoading,
  };
}
